import { useRootStore } from 'stores/RootStore'
import { UseInfiniteQueryOptions, useInfiniteQuery } from 'react-query'
import { Response } from 'request-dot-js/index'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { reportError } from 'utils/error'
import { getNextPageParam } from 'utils/getNextPageParam'
import qs from 'qs'
import { VisualElement } from 'api/clients/VisualElementClient'

interface Props extends Filters {
  items?: number
}

interface Filters {
  width: number
  height: number
  source_id?: number
  element_type?: 'Template' | 'Element'
  category?: string[]
  type?: string[]
  style?: string[]
  distinct_on_binder?: boolean
  order?: 'newest'
  query?: string
}

type Data = { visual_elements: VisualElement[] }

export const useRecentVisualTemplates = (
  { width, height, items = 4, element_type, source_id, category, type, style, distinct_on_binder, order, query }: Props,
  options?: Partial<UseInfiniteQueryOptions<Response<Data>>>,
) => {
  const queryKey = [
    'visualTemplates',
    'recent',
    { width, height, source_id, element_type, category, type, style, distinct_on_binder, order, query },
  ]
  const { visualElements } = useRootStore()

  const fetchRecentVisualElements = async (params: string) => {
    return sendToApi<Data>(`/visual_elements/recent?${params}`).then((response) => {
      if (response.type === SUCCESS_RESPONSE_TYPE) {
        visualElements.bulkAdd(response.data.visual_elements)
        return Promise.resolve(response)
      } else {
        return Promise.reject(response)
      }
    })
  }

  return useInfiniteQuery<Response<Data>>(
    queryKey,
    ({ pageParam = 1, queryKey: [_, __, filters] }) => {
      const { width, height, source_id, element_type, category, type, style, query } = filters as Filters
      const params = qs.stringify(
        {
          width,
          height,
          items,
          page: pageParam,
          source_id,
          element_type,
          category,
          type,
          style,
          distinct_on_binder,
          order,
          query,
        },
        { arrayFormat: 'brackets' },
      )
      return fetchRecentVisualElements(params)
    },
    {
      ...options,
      retry: false,
      refetchOnWindowFocus: false,
      getNextPageParam,
      onError: (err) => {
        reportError(err, {
          severity: 'error',
        })
      },
    },
  )
}
